import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function PrivacyPolicyPage() {
  const { currentTheme } = useTheme();

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: currentTheme.foreground, fontFamily: 'Playfair Display, serif' }}
            >
              Privacy Policy
            </h1>
            <p style={{ color: currentTheme.foreground, opacity: 0.7 }}>
              Last updated: March 2026
            </p>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            style={{ color: currentTheme.foreground }}
          >
            {/* Introduction */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                This Privacy Policy explains how Nspire Living Healthy ("we," "us," or "our") applications handle information, including data collected through third-party services. We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Information We Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                While our applications do not directly collect personal information, third-party services integrated into our apps may collect certain data:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Device identifiers for advertising purposes</li>
                <li>Usage analytics and interaction data</li>
                <li>IP address and general location information</li>
                <li>Payment information (processed securely by payment providers)</li>
              </ul>
            </section>

            {/* Third-Party Advertising */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Third-Party Advertising
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications display advertisements provided by third-party advertising networks. These networks may use cookies, device identifiers, and similar technologies to collect information about your device and app usage to serve personalized advertisements. This may include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Advertising ID (IDFA for iOS, GAID for Android)</li>
                <li>Device type and operating system</li>
                <li>IP address and general network information</li>
                <li>Interaction with advertisements</li>
                <li>Approximate geographic location</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                Our applications use Google AdMob as an advertising mediation platform. Google AdMob and its demand partners may collect your IP address and Advertising ID to serve relevant ads and measure ad performance. For more details on how Google AdMob handles your data, please review the Google AdMob Privacy Policy.
              </p>
              <p className="mb-4 leading-relaxed">
                You may opt out of personalized advertising through your device settings or by following the instructions provided by the advertising networks.
              </p>
            </section>

            {/* Payment Processing */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Payment Processing
              </h2>
              <p className="mb-4 leading-relaxed">
                For in-app purchases and subscriptions, we use third-party payment processors (such as Apple App Store, Google Play Store, or other payment providers). These processors handle all payment transactions securely. We do not store or have access to your complete payment card details. Payment processors may collect:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Payment card information (handled securely by the processor)</li>
                <li>Billing address</li>
                <li>Transaction history</li>
                <li>Account identifiers</li>
              </ul>
            </section>

            {/* Local Data Storage */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Local Data Storage
              </h2>
              <p className="mb-4 leading-relaxed">
                App preferences and user-generated content within our applications are stored locally on your device. This local data never leaves your device and is not accessible to us. You have full control over this local data and can delete it by uninstalling the application or clearing the app's data through your device settings.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Third-Party Services
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications integrate with third-party services that have their own privacy policies. We encourage you to review the privacy policies of these services:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Google AdMob / Google Ads</li>
                <li>Apple App Store</li>
                <li>Google Play Store</li>
                <li>Other advertising and analytics partners</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Children's Privacy
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications are not directed at children under 13. We do not knowingly collect personal information from children. If you believe a child has provided information through our apps, please contact us so we can take appropriate action.
              </p>
            </section>

            {/* Your Choices */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Your Choices
              </h2>
              <p className="mb-4 leading-relaxed">
                You have options regarding data collection:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Opt out of personalized ads via device settings</li>
                <li>Reset your advertising identifier</li>
                <li>Limit ad tracking on your device</li>
                <li>Uninstall the application to remove local data</li>
              </ul>
            </section>

            {/* Changes to This Policy */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Changes to This Policy
              </h2>
              <p className="mb-4 leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be reflected on this page with an updated revision date. We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Contact Us */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Contact Us
              </h2>
              <p className="mb-4 leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us through the main application's support channels.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
